// src/contexts/ToastContext.tsx
import React, { createContext, useContext, useState, useCallback } from "react";
import Toast, {
  ToastType,
  ToastPosition,
  ToastAction,
} from "../components/common/Toast";

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  position?: ToastPosition;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (toast: Omit<ToastItem, "id">) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// 生成唯一 ID
const generateId = () =>
  `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Toast Provider 組件
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // 添加 Toast
  const showToast = useCallback((toast: Omit<ToastItem, "id">) => {
    const id = generateId();
    const newToast: ToastItem = {
      id,
      duration: 4000,
      position: "bottom-right",
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);

    // 註：Toast組件會在適當時機調用dismissToast來清理，不需要備援清理邏輯
  }, []);

  // 便捷方法
  //
  // 現代 UX 使用原則：
  // - 適合用 Toast：非可見操作（背景寄信、隱性儲存）、破壞性操作的 Undo 提示、錯誤訊息
  // - 不適合用 Toast：結果已即時可見的操作（UI 已更新 → 用戶能直接看到結果）
  //
  // success Toast 尤其容易被濫用。若操作結果已反映在畫面上，請省略 showSuccess，
  // 讓 UI 本身說話，減少視覺噪音。
  const showSuccess = useCallback(
    (title: string, message?: string) => {
      showToast({ type: "success", title, message });
    },
    [showToast],
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      showToast({ type: "error", title, message, duration: 6000 }); // 錯誤訊息顯示較久
    },
    [showToast],
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      showToast({ type: "warning", title, message, duration: 5000 });
    },
    [showToast],
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      showToast({ type: "info", title, message });
    },
    [showToast],
  );

  // 手動移除 Toast
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // 移除所有 Toast
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextValue = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissToast,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* 渲染所有 Toast */}
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={dismissToast} />
      ))}
    </ToastContext.Provider>
  );
};

// Hook for using Toast context
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export default ToastContext;
